import { kEmailSelector } from "@protontech/autofill/constants/selectors";
import { feature, flattenFeatures } from "@protontech/autofill/features/feature";
import type { FormFeatures } from "@protontech/autofill/features/v1/abstract.form";
import { formFeaturesComputer } from "@protontech/autofill/features/v1/forms.sorted.gen";
import type { ComputedFeatures } from "@protontech/autofill/types";
import { any } from "@protontech/autofill/utils/combinators";
import { matchCCFieldCandidate } from "@protontech/autofill/utils/credit-card";
import { getFieldHaystacks } from "@protontech/autofill/utils/extract";
import { getFormClassification, getParentFormFnode } from "@protontech/autofill/utils/fathom";
import { flagAsHidden, removeHiddenFlag } from "@protontech/autofill/utils/flags";
import { matchIdentityField } from "@protontech/autofill/utils/identity";
import { matchEmailAttr, matchSearchAction, matchUsernameAttr } from "@protontech/autofill/utils/re";
import { isVisible, isVisibleField } from "@protontech/autofill/utils/visible";

/** Safe to export and re-use without attaching to the FNode notes as
 * these are direct DOM property accessors. For any computed feature,
 * prefer reading them from the `FieldFeature` note for performance. */
export const field = feature("field", {}, (_, fnode) => fnode.element as HTMLInputElement);
export const value = feature("value", { field }, (p) => p.field.value);
export const type = feature("type", { field }, (p) => p.field.getAttribute("type"));
export const readonly = feature("readonly", { field }, (p) => p.field.readOnly);
export const required = feature("required", { field }, (p) => p.field.required);
export const minLength = feature("minLength", { field }, (p) => p.field.getAttribute("minlength"));
export const maxLength = feature("maxLength", { field }, (p) => p.field.getAttribute("maxlength"));
export const pattern = feature("pattern", { field }, (p) => p.field.pattern);
export const disabled = feature("disabled", { field }, (p) => p.field.disabled);
export const tabIndex = feature("tabIndex", { field }, (p) => p.field.tabIndex);
export const placeholder = feature("placeholder", { field }, (p) => p.field.placeholder);
export const typeValid = feature("typeValid", { fieldType: type }, (p) => p.type !== "hidden");

/* this FNode may be missing its form features if we resolved a pre-detected
 * form FNode as it will have been omitted from the classification pipeline.
 * Fields may need them either way so hydrate the note for `form` accordingly */
export const parentFormFnode = feature("parentFormFnode", {}, (_, fnode) => getParentFormFnode(fnode));
export const parentFormFeatures = feature("parentFormFeatures", { parentFormFnode }, (p) => {
  const parent = p.parentFormFnode;
  if (parent && !parent.hasNoteFor("form")) parent.setNoteFor("form", formFeaturesComputer.compute(parent));
  return parent?.noteFor<FormFeatures>("form");
});

export const parentFormInputIterator = feature("parentFormInputIterator", { parentFormFeatures }, (p) => p.parentFormFeatures?.inputIterator ?? null);
export const formClassification = feature("formClassification", { parentFormFnode }, (p) => getFormClassification(p.parentFormFnode));
export const isFormLogin = feature("isFormLogin", { formClassification }, (p) => p.formClassification.login);
export const isFormRegister = feature("isFormRegister", { formClassification }, (p) => p.formClassification.register);
export const isFormPasswordChange = feature("isFormPasswordChange", { formClassification }, (p) => p.formClassification.passwordChange);
export const isFormRecovery = feature("isFormRecovery", { formClassification }, (p) => p.formClassification.recovery);
export const isFormNoop = feature("isFormNoop", { formClassification }, (p) => p.formClassification.noop);

export const autocomplete = feature("autocomplete", { field }, (p) => p.field.getAttribute("autocomplete")?.trim().toLowerCase());

/* VISIBILITY : do not account for opacity when checking for
 * visibility (certain website like `airbnb.com` add `opacity: 0`
 * to fields which have not been touched yet) */
export const visible = feature("visible", { typeValid, field }, (p) => (p.typeValid ? isVisibleField(p.field) && isVisible(p.field, { opacity: false }) : false));

/** SIDE-EFFECT */
export const flagHiddenFieldsSideEffect = feature("flagHiddenFieldsSideEffect", { typeValid, visible, field }, (p) => {
  if (p.typeValid) (p.visible ? removeHiddenFlag : flagAsHidden)(p.field);
});

/* HAYSTACKS */
export const fieldHaystacks = feature("fieldHaystacks", { field }, (p) => getFieldHaystacks(p.field));
export const attrs = feature("attrs", { fieldHaystacks }, (p) => p.fieldHaystacks.fieldAttrs);
export const text = feature("text", { fieldHaystacks }, (p) => p.fieldHaystacks.fieldText);
export const label = feature("label", { fieldHaystacks }, (p) => p.fieldHaystacks.labelText);

/* OUTLIER */
export const searchField = feature("searchField", { visible, attrs, text, flagHiddenFieldsSideEffect /* execute side-effect */ }, (p) => p.visible && any(matchSearchAction)(p.attrs.concat(p.text)));
export const isCC = feature("isCC", { field, visible }, (p) => matchCCFieldCandidate(p.field, { visible: p.visible }));
export const isIdentity = feature(
  "isIdentity",
  { isCC, field, visible, formClassification, searchField, fieldType: type },
  (p) => !p.isCC && matchIdentityField(p.field, { visible: p.visible, form: p.formClassification, searchField: p.searchField, type: p.type }),
);

/* SIBLINGS */
export const prevInput = feature("prevInput", { typeValid, parentFormInputIterator, field }, (p) => (p.typeValid ? (p.parentFormInputIterator?.prev(p.field, "INPUT") ?? null) : null));
export const prevField = feature("prevField", { typeValid, parentFormInputIterator, field }, (p) => (p.typeValid ? (p.parentFormInputIterator?.prev(p.field) ?? null) : null));
export const nextField = feature("nextField", { typeValid, parentFormInputIterator, field }, (p) => (p.typeValid ? (p.parentFormInputIterator?.next(p.field) ?? null) : null));

export const fieldFeatures = flattenFeatures({
  attrs,
  autocomplete,
  isCC,
  isFormLogin,
  isFormNoop,
  isFormPasswordChange,
  isFormRecovery,
  isFormRegister,
  isIdentity,
  label,
  nextField,
  parentFormFeatures,
  parentFormFnode,
  prevField,
  prevInput,
  searchField,
  text,
  visible,
});

export type FieldFeatures = ComputedFeatures<typeof fieldFeatures>;

/** Shared features */
export const fieldFeats = feature("fieldFeats", {}, (_, fnode) => fnode.noteFor<FieldFeatures>("field")!);
export const fieldForm = feature("fieldForm", { fieldFeats }, (p) => p.fieldFeats.parentFormFnode);
export const fieldCC = feature("fieldCC", { fieldFeats }, (p) => p.fieldFeats.isCC);
export const fieldIdentity = feature("fieldIdentity", { fieldFeats }, (p) => p.fieldFeats.isIdentity);
export const fieldSearch = feature("isSearchField", { fieldFeats }, (p) => p.fieldFeats.searchField);
export const fieldAttrs = feature("fieldAttrs", { fieldFeats }, (p) => p.fieldFeats.attrs);
export const fieldText = feature("fieldText", { fieldFeats }, (p) => p.fieldFeats.text);
export const fieldLabel = feature("fieldLabel", { fieldFeats }, (p) => p.fieldFeats.label);
export const fieldAutocomplete = feature("fieldAutocomplete", { fieldFeats }, (p) => p.fieldFeats.autocomplete);
export const fieldEmailMatch = feature("emailMatch", { fieldAttrs, fieldLabel, fieldText, field }, (p) => p.field.matches(kEmailSelector) || any(matchEmailAttr)(p.fieldAttrs.concat(p.fieldText, p.fieldLabel)));
export const fieldAutocompleteUsername = feature("autocompleteUsername", { fieldAutocomplete, fieldEmailMatch }, (p) => !p.emailMatch && Boolean(p.fieldAutocomplete?.includes("username") ?? p.fieldAutocomplete?.includes("nickname")));
export const fieldAttrEmail = feature("attrEmail", { fieldAttrs }, (p) => any(matchEmailAttr)(p.fieldAttrs));
export const fieldAttrUsername = feature("attrUsername", { fieldAttrs }, (p) => any(matchUsernameAttr)(p.fieldAttrs));
export const fieldFormFeats = feature("fieldFormFeats", { fieldFeats }, (p) => p.fieldFeats.parentFormFeatures);
export const fieldAutocompleteEmail = feature("autocompleteEmail", { fieldAutocomplete }, (p) => p.fieldAutocomplete?.includes("email") ?? false);
export const fieldAutocompleteOff = feature("autocompleteOff", { fieldAutocomplete }, (p) => p.fieldAutocomplete === "false" || p.fieldAutocomplete === "off");
export const fieldNext = feature("fieldNext", { fieldFeats }, (p) => p.fieldFeats.nextField);
export const fieldPrev = feature("fieldPrev", { fieldFeats }, (p) => p.fieldFeats.prevField);
export const fieldPrevInput = feature("fieldPrevInput", { fieldFeats }, (p) => p.fieldFeats.prevInput);
export const fieldLogin = feature("fieldLogin", { fieldFeats }, (p) => p.fieldFeats.isFormLogin);
export const fieldRegister = feature("fieldRegister", { fieldFeats }, (p) => p.fieldFeats.isFormRegister);
export const fieldExotic = feature("fieldExotic", { fieldFeats }, (p) => p.fieldFeats.isFormNoop);
